import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import useSWR from 'swr';
import { fetchBrand, searchBrands } from '@/api';
import Bugsnag from '@/bugsnag';
import AppBanner from '@/components/app-banner';
import Brand from '@/components/brand';
import AboutBrand from '@/components/brand/about-brand';
import {
  deliveryKeyForBrand,
  formatMoney,
  getBreadcrumbItems,
  getPriceSummary,
  headingKeyForBrand,
  isPurchasable,
  toPlainText,
  typeKeyForBrand,
} from '@/components/brand/brand-content';
import BrandFAQ from '@/components/brand-faq';
import EarlyExit from '@/components/early-exit';
import HowItWorks from '@/components/how-it-works';
import { BreadcrumbSchema, ProductSchema } from '@/components/json-ld';
import MaintenanceNotice, { maintenancePageProps } from '@/components/maintenance-notice';
import OtherBrandsInCountry from '@/components/other-brands-in-country';
import SEO from '@/components/seo';
import { SectionContainer } from '@/components/ui';
import config from '@/config';
import i18n from '@/i18n.json';
import { loadMessages } from '@/lib/messages';
import BrandStore from '@/stores/brand-store';
import { useStore } from '@/stores/store-provider';
import { alpha, media } from '@/themes';
import { getAllBrandsAndTopupsFromResults, parseIPAddress } from '@/utils';
import { countryNameInSentence } from '@/utils/country-name';
import { shareImage } from '@/utils/share-image';
import { trackViewedBrandDetails } from '../../tracker';

const SearchField = dynamic(() => import('@/components/search-field'), { ssr: false });
const Toolbar = dynamic(() => import('@/components/toolbar'), { ssr: false });

const Search = styled(SearchField)`
  background: var(--muted);
  border: 1px solid transparent;
  border-radius: .25em;
  height: 100%;
  margin: auto;
  max-width: 30em;

  ${media.sm`
    display: none !important;
  `};

  &.search-field--focused {
    background: var(--background);
    border-color: ${(props) => props.theme.colors.primary};
    box-shadow: 0 0 0 .15em ${(props) => alpha(props.theme.colors.primary, 40)};
  }
`;

const BrandDetails = ({ initialBrand, initialOtherBrands, canonicalUrl, router }) => {
  const { data: brand, error } = useSWR(
    [initialBrand.country.slug, initialBrand.slug],
    fetchBrand,
    { fallbackData: initialBrand, revalidateOnMount: false, revalidateIfStale: false }
  );
  error && console.log(`error`, error);
  const theme = useTheme();
  const cart = useStore();
  const [store] = useState(() => new BrandStore(brand, cart, null));
  const t = useTranslations('brands');
  const brandT = useTranslations('brand');
  const shareImageUrl = shareImage.brand(brand.country.slug, brand.slug, router.locale);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [cachedSuggestions, setCachedSuggestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Everything below is derived from the brand record: no hand-written brand copy.
  const typeKey = typeKeyForBrand(brand);
  const headingKey = headingKeyForBrand(brand);
  const productName = brandT('heading', { type: headingKey, brandName: brand.name });
  const summary = brandT('summary', {
    type: typeKey,
    delivery: deliveryKeyForBrand(brand),
    country: countryNameInSentence(brand.country, router.locale),
  });
  const prices = getPriceSummary(brand.products);
  const money = (amountInCents) => formatMoney(amountInCents, prices.currency, router.locale);
  let priceKind = 'none';

  if (prices) {
    priceKind = prices.lowInCents === prices.highInCents ? 'single' : 'range';
  }

  const seoTitle = t('seoTitle', { type: headingKey, brandName: brand.name });
  const seoDescription = t('seoDescription', {
    type: headingKey,
    brandName: brand.name,
    summary,
    price: priceKind,
    low: prices ? money(prices.lowInCents) : '',
    high: prices ? money(prices.highInCents) : '',
  });

  const handleBlur = () => {
    setCachedSuggestions(suggestions);
    setTimeout(() => setSuggestions([]), 250);
  };

  const handleFocus = () => {
    if (cachedSuggestions.length > 0) {
      setSuggestions(cachedSuggestions);
    }
  };

  const handleSearchTerm = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleOnChange = async (event) => {
    const { value } = event.target;
    setSuggestions([]);

    if (value.trim()) {
      setIsLoadingSuggestions(true);
      const { results } = await searchBrands({ $search: value.trim() });
      const suggestions = getAllBrandsAndTopupsFromResults(results);

      setSuggestions(
        suggestions.sort((brand) => {
          if (brand.name.toLowerCase().startsWith(value.toLowerCase())) {
            return -1;
          } else {
            return 0;
          }
        })
      );
      setIsLoadingSuggestions(false);
    }
  };

  useEffect(() => {
    store.setProduct(null);
    store.setAmount(0);
  }, [store]);

  useEffect(() => {
    store.setBrand(brand);
  }, [brand, store]);

  useEffect(() => {
    trackViewedBrandDetails(initialBrand);
  }, [initialBrand]);

  // Same trail as the visible breadcrumb (components/brand.js), as absolute URLs.
  const localeRoot =
    router.locale && router.locale !== i18n.defaultLocale
      ? `${config.rootUrl}/${router.locale}`
      : config.rootUrl;
  const breadcrumbItems = getBreadcrumbItems(brand).map((item) => ({
    name: item.name,
    url: item.href ? `${localeRoot}${item.href}` : canonicalUrl,
  }));

  return (
    <>
      <SEO
        title={seoTitle}
        description={seoDescription}
        url={canonicalUrl}
        imageUrl={shareImageUrl}
        imageAlt={productName}
        hreflangPath={`/brands/${brand.country.slug}/${brand.slug}`}
      />
      <ProductSchema
        name={productName}
        description={toPlainText(brand.description) || summary}
        image={brand.imageUrl}
        url={canonicalUrl}
        brandName={brand.name}
        category={brand.categories?.[0]?.name || brandT('productKind', { type: typeKey })}
        countryName={brand.country.name}
        prices={prices}
        inStock={isPurchasable(brand)}
      />
      <BreadcrumbSchema items={breadcrumbItems} />
      <SectionContainer>
        <Toolbar>
          <Search
            isLoadingSuggestions={isLoadingSuggestions}
            onBlur={handleBlur}
            onChange={handleOnChange}
            onFocus={handleFocus}
            search={() => {}}
            searchTerm={searchTerm}
            setSearchTerm={handleSearchTerm}
            suggestions={suggestions}
          />
        </Toolbar>
        {brand && (
          <Brand
            key={brand.id}
            brand={brand}
            store={store}
            products={brand.products}
            router={router}
          />
        )}
      </SectionContainer>
      {brand && <AboutBrand brand={brand} />}
      {brand && <OtherBrandsInCountry brand={brand} initialBrands={initialOtherBrands} />}
      {brand && <HowItWorks type={brand.type} />}
      {brand && <BrandFAQ brand={brand} />}
      <AppBanner background={theme.colors.primary} inverted source="brand_details" />
    </>
  );
};

const Index = ({ initialBrand, initialOtherBrands, canonicalUrl }) => {
  const router = useRouter();

  return (
    <EarlyExit
      Component={BrandDetails}
      condition={router.isFallback}
      initialBrand={initialBrand}
      initialOtherBrands={initialOtherBrands}
      canonicalUrl={canonicalUrl}
    />
  );
};

export async function getServerSideProps({ params, req, locale }) {
  if (req.headers['x-bb-ip'] === '92.0.84.169') {
    return {
      redirect: {
        permanent: false,
        destination: 'https://google.com',
      },
    };
  }

  if (config.inMaintenanceMode) {
    return maintenancePageProps(locale);
  }

  //TODO: Error handling and log to Bugsnag
  const countrySlug = params.slug[0];
  const slug = params.slug[1];
  // let brand = enabledBrands.find(b => b.country.slug === countrySlug && b.slug === slug);
  const brand = await fetchBrand([countrySlug, slug]);
  if (!brand) {
    Bugsnag.notify(new Error(`Brand not found for ${countrySlug}/${slug}`), (event) => {
      event.addMetadata('headers', req.headers);
      event.addMetadata('other', { ipAddress: parseIPAddress(req) });
      return req.headers.from !== 'tech@babbar.tech';
    });
    return { notFound: true };
  }

  // "Other brands in this country" links, from the build data: no network, so they
  // cost the response nothing and are in the HTML. Required here so the catalogue
  // never reaches the client bundle. Null (country not in the build data) makes the
  // component load them in the browser instead.
  let initialOtherBrands = null;

  try {
    const { enabledBrands } = require('@data/index');
    const { otherBrandsInCountry } = require('@/utils/country-catalogue');

    initialOtherBrands = otherBrandsInCountry({
      brands: enabledBrands,
      countryId: brand.countryId || brand.country?.id,
      excludeId: brand.id,
    });
  } catch (error) {
    Bugsnag.notify(error);
  }

  const pathSegment = `brands/${countrySlug}/${slug}`;
  const canonicalUrl =
    locale === i18n.defaultLocale
      ? `${config.rootUrl}/${pathSegment}`
      : `${config.rootUrl}/${locale}/${pathSegment}`;

  return {
    props: {
      initialBrand: brand,
      initialOtherBrands,
      canonicalUrl,
      messages: loadMessages(locale),
    },
  };
}

const BrandPage = (props) => (props.maintenance ? <MaintenanceNotice /> : <Index {...props} />);

export default BrandPage;
